import { useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, DatePicker, Drawer, Input, InputNumber } from 'antd';
import heIL from 'antd/locale/he_IL';
import dayjs, { type Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { Money } from '../../components/Money';
import { useCreateClosing } from '../../hooks/useCashFlow';
import { todayIsrael } from '../../utils/dates';
import type { Receipt } from '@deposits/shared';

interface Props {
  open: boolean;
  registerId: number | null;
  registerName: string;
  /** The register's standing float = opening balance. */
  openingBalance: number;
  /** The cash receipts the user chose to close (subset of the register). */
  selectedReceipts: Receipt[];
  onClose: () => void;
  /** Called with the new closing id so the parent can open its PDF. */
  onCreated: (closingId: number) => void;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function CloseRegisterDrawer({
  open,
  registerId,
  registerName,
  openingBalance,
  selectedReceipts,
  onClose,
  onCreated,
}: Props) {
  const createClosing = useCreateClosing();

  const cashSalesTotal = useMemo(
    () => round2(selectedReceipts.reduce((s, r) => s + r.amount, 0)),
    [selectedReceipts],
  );
  const expected = round2(openingBalance + cashSalesTotal);

  const [closingDate, setClosingDate] = useState<Dayjs>(() => dayjs(todayIsrael()));
  const [counted, setCounted] = useState<number>(expected);
  const [floatLeft, setFloatLeft] = useState<number>(openingBalance);
  const [notes, setNotes] = useState('');
  const [touchedCount, setTouchedCount] = useState(false);

  // Re-seed the form each time the drawer opens (or the selection/expected
  // changes before the user has touched the count).
  useEffect(() => {
    if (open) {
      setClosingDate(dayjs(todayIsrael()));
      setNotes('');
      setTouchedCount(false);
      setCounted(expected);
      setFloatLeft(openingBalance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && !touchedCount) setCounted(expected);
  }, [open, expected, touchedCount]);

  const variance = round2(counted - expected);
  const amountToSafe = round2(counted - floatLeft);

  const submit = () => {
    if (registerId == null) return;
    if (amountToSafe < 0) {
      toast.error('הסכום שנותר בקופה גדול מהסכום שנספר');
      return;
    }
    createClosing.mutate(
      {
        cash_register_id: registerId,
        closing_date: closingDate.format('YYYY-MM-DD'),
        external_receipt_ids: selectedReceipts.map((r) => r.external_receipt_id),
        counted_amount: counted,
        float_left: floatLeft,
        notes: notes.trim() || null,
      },
      {
        onSuccess: (res) => {
          toast.success(`סגירת קופה מספר ${res.closing_number} נוצרה`);
          if (!res.pdf_url) toast.error('הסגירה נוצרה אך נכשלה הפקת ה-PDF');
          onCreated(res.id);
        },
        onError: (err) => {
          toast.error(err.message || 'שגיאה בסגירת הקופה');
          if (err.message?.includes('אינם זמינים')) onClose();
        },
      },
    );
  };

  return (
    <Drawer
      title={<span className="text-heading-3-bold text-main-text">סגירת קופה</span>}
      placement="left"
      open={open}
      onClose={onClose}
      width={520}
      closable={false}
      styles={{ header: { borderBottom: 'none' } }}
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        <div className="flex flex-col gap-5">
          <div className="bg-blue-bg border border-border rounded p-4">
            <div className="font-semibold mb-3">{registerName}</div>
            <div className="grid grid-cols-2 gap-y-2 gap-x-3 text-base">
              <Row label="עודף פתיחה"><Money amount={openingBalance} /></Row>
              <Row label={`מזומן שנבחר (${selectedReceipts.length} תקבולים)`}>
                <Money amount={cashSalesTotal} />
              </Row>
              <Row label="צפי בקופה" bold><Money amount={expected} /></Row>
            </div>
          </div>

          {selectedReceipts.length === 0 && (
            <div className="border border-border rounded p-3 text-small text-secondary-text">
              סגירה על 0 — אין תקבולי מזומן במשמרת. ייווצר דוח סגירה לתיעוד; כשאין סכום להעברה לכספת, לא נוצרת מעטפה בכספת.
            </div>
          )}

          <Field label="תאריך סגירה" required>
            <DatePicker
              value={closingDate}
              onChange={(d) => d && setClosingDate(d)}
              format="DD/MM/YYYY"
              allowClear={false}
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="נספר בפועל" required>
            <InputNumber
              value={counted}
              onChange={(v) => {
                setCounted(Number(v ?? 0));
                setTouchedCount(true);
              }}
              min={0}
              addonAfter="₪"
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="נשאר בקופה (עודף פתיחה)">
            <InputNumber
              value={floatLeft}
              onChange={(v) => setFloatLeft(Number(v ?? 0))}
              min={0}
              addonAfter="₪"
              style={{ width: '100%' }}
            />
          </Field>

          <div className="border border-border rounded p-4 bg-white flex flex-col gap-2">
            <Row label="הפרש (עודף/חוסר)">
              <span className={variance === 0 ? '' : variance > 0 ? 'text-success-green' : 'text-error-red'}>
                <Money amount={variance} />
              </span>
            </Row>
            <Row label="יועבר לכספת" bold><Money amount={amountToSafe} /></Row>
          </div>

          <Field label="הערות">
            <Input.TextArea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              rows={2}
              placeholder="פירוט אופציונלי..."
            />
          </Field>

          <div className="flex justify-end gap-3">
            <Button onClick={onClose}>ביטול</Button>
            <Button
              type="primary"
              onClick={submit}
              loading={createClosing.isPending}
            >
              סגור קופה
            </Button>
          </div>
        </div>
      </ConfigProvider>
    </Drawer>
  );
}

function Row({ label, children, bold }: { label: string; children: React.ReactNode; bold?: boolean }) {
  return (
    <>
      <div className="text-secondary-text">{label}</div>
      <div className={`text-end ${bold ? 'font-semibold text-heading-3' : ''}`}>{children}</div>
    </>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-small text-main-text mb-1">
        {label}
        {required && <span className="text-error-red"> *</span>}
      </div>
      {children}
    </div>
  );
}
